import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ParticleCanvas from '../components/ParticleCanvas';
import { useTheme } from '../context/ThemeContext';
/* ─── Design tokens ──────────────────────────────────────────────── */
const BG        = '#020C1B';
const BG2       = '#041525';
const CARD      = 'rgba(190,81,3,0.05)';
const CARD_HOVER= 'rgba(190,81,3,0.09)';
const BORDER    = 'rgba(190,81,3,0.15)';
const BORDER2   = 'rgba(190,81,3,0.32)';
const TEXT      = '#fff';
const MUTED     = 'rgba(255,255,255,0.55)';
const ACCENT    = '#BE5103';
const PURPLE    = '#FFCE1B';
const CYAN      = '#069494';
const RUST      = '#B7410E';
const FEATURE_PURPLE = '#8B5CF6';
const ORANGE    = '#F97316';
const RED       = '#EF4444';

/* ─── Data ───────────────────────────────────────────────────────── */
const pricingPlans = [
  {
    name: 'Starter',
    blurb: 'Solo operators',
    price: '₹2,999',
    period: '/mo',
    badge: null,
    cta: 'Get started',
    features: ['Core AI automation', 'WhatsApp automation', 'Basic analytics', 'Email support'],
  },
  {
    name: 'Growth',
    blurb: 'Small teams',
    price: '₹7,999',
    period: '/mo',
    badge: null,
    cta: 'Get started',
    features: ['Social + Ads + AI calling', 'GEO visibility', 'CRM sync', 'Priority support'],
  },
  {
    name: 'Pro',
    blurb: 'Serious operators',
    price: '₹14,999',
    period: '/mo',
    badge: 'most-popular',
    cta: 'Start free trial',
    features: ['Full stack automation', 'WhatsApp Commerce', 'WebBuilder', 'Dedicated manager'],
  },
  {
    name: 'Enterprise',
    blurb: 'Custom scale',
    price: 'Talk to us',
    period: '',
    badge: null,
    cta: 'Contact sales',
    features: ['All agents', 'White-label ready', 'SLA guarantee', 'On-premise option'],
  },
  {
    name: 'Agency',
    blurb: 'Multi-client scale',
    price: '₹49,999',
    period: '/mo',
    badge: null,
    cta: 'Contact sales',
    features: ['Multi-client seats', 'White-label dashboard', 'SLA + custom integrations', 'Dedicated partner manager'],
  },
];

const testimonials = [
  {
    name: 'Arjun Mehta',
    role: 'Founder, GrowthStack',
    text: 'ODC replaced 3 tools and an intern. ROI in week 1.',
    avatar: '🧑‍💻',
    industry: '🏠 Real Estate',
  },
  {
    name: 'Priya Sharma',
    role: 'CMO, NexaRetail',
    text: 'Our leads doubled in 30 days. The AI agents just work.',
    avatar: '👩‍💼',
    industry: '🍽️ Restaurant',
  },
  {
    name: 'Rahul Gupta',
    role: 'CEO, ScaleUp.io',
    text: 'I sleep 8 hours now. ODC handles everything overnight.',
    avatar: '🧑‍🚀',
    industry: '💻 Technology',
  },
] as const;

const landingFaqPreview = [
  {
    id: 'lp1',
    q: 'How long does setup take?',
    a: 'Just 30 minutes. Guided onboarding captures your business DNA, brand, and goals, then your agents run automatically.',
  },
  {
    id: 'lp2',
    q: 'Kya mujhe technical knowledge chahiye?',
    a: 'No technical expertise is required. If you can use WhatsApp, you can run ODC—no coding or server setup needed.',
  },
  {
    id: 'lp3',
    q: 'Is there a free trial?',
    a: 'Yes, a 14-day free trial is available. No credit card required. Setup fee is ₹0. Cancel anytime.',
  },
  {
    id: 'lp4',
    q: 'What is the refund policy?',
    a: '7 din full refund (terms apply). Trial ke andar risk zero — support se connect ho sakte ho.',
  },
] as const;

const mainFeatureCards = [
  {
    icon: '📱',
    title: 'Social Media Autopilot',
    body: 'Instagram, Facebook, YouTube, LinkedIn — AI publishes daily content automatically.',
    border: `1px solid rgba(190,81,3,0.35)`,
    items: ['Daily posts auto-schedule', 'Platform-specific content', 'Trending hashtags auto', 'Reel + Story + Carousel'],
  },
  {
    icon: '🎯',
    title: 'Lead Generation Machine',
    body: 'Discover prospects, score them, and follow up automatically — without manual effort.',
    border: `1px solid rgba(6,148,148,0.35)`,
    items: ['AI lead scoring (0-100)', 'Auto WhatsApp follow-up', 'Lead database auto-build', 'Hot leads — instant alert'],
  },
  {
    icon: '📞',
    title: 'AI Calls — Aapki Awaaz Mein',
    body: 'ElevenLabs voice clone se aapki exact awaaz mein calls. 9AM-6PM daily. Hindi, Urdu, English.',
    border: `1px solid rgba(139,92,246,0.35)`,
    items: ['Voice clone in 10 minutes', 'Objection handling auto', 'Appointment booking', 'TRAI DND compliant'],
  },
  {
    icon: '🔍',
    title: 'SEO + GEO Visibility',
    body: 'Rank on Google and improve citations in ODC AI. Covers both traditional and AI search.',
    border: `1px solid rgba(245,158,11,0.35)`,
    items: ['Google rankings auto-improve', 'ODC AI visibility score', 'Google My Business manage', 'Voice search optimize'],
  },
  {
    icon: '💬',
    title: 'WhatsApp Automation',
    body: 'Broadcasts, orders, follow-ups, and customer support — all automated on WhatsApp.',
    border: `1px solid rgba(190,81,3,0.28)`,
    items: ['Bulk personalized broadcasts', 'Auto order management', '24/7 WhatsApp chatbot', 'UPI payment collection'],
  },
  {
    icon: '📊',
    title: 'Analytics & Reports',
    body: 'Track every metric with weekly email reports and transparent ROI insights.',
    border: `1px solid rgba(6,148,148,0.28)`,
    items: ['Real-time dashboard', 'ROI per channel', 'Competitor tracking', 'Weekly email digest'],
  },
] as const;

const planCompareRows: { feature: string; starter: boolean; growth: boolean; pro: boolean; enterprise: boolean; agency: boolean }[] = [
  { feature: 'Social Media Posts', starter: true, growth: true, pro: true, enterprise: true, agency: true },
  { feature: 'AI Calling', starter: false, growth: true, pro: true, enterprise: true, agency: true },
  { feature: 'WhatsApp Commerce', starter: false, growth: false, pro: true, enterprise: true, agency: true },
  { feature: 'GEO Visibility', starter: false, growth: true, pro: true, enterprise: true, agency: true },
  { feature: 'WebBuilder', starter: false, growth: false, pro: true, enterprise: true, agency: true },
  { feature: 'White Label', starter: false, growth: false, pro: false, enterprise: false, agency: true },
];

/* ─── Helpers ────────────────────────────────────────────────────── */
function gradientText(g: string): React.CSSProperties {
  return { background: g, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' };
}

/* ─── Intersection-observer hook (for scroll-triggered animations) ─ */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

/* ─── Section wrapper with fade-in ─────────────────────────────── */
function FadeSection({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(28px)',
        transition: 'opacity 0.65s cubic-bezier(0.4,0,0.2,1), transform 0.65s cubic-bezier(0.4,0,0.2,1)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LANDING PAGE
═══════════════════════════════════════════════════════════════════ */
export function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [landingFaqOpen, setLandingFaqOpen] = useState<string | null>('lp1');
  const { theme: colorMode, toggle } = useTheme();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* shared card style */
  const card: React.CSSProperties = {
    background: CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: 20,
    padding: 24,
    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
  };

  return (
    <>
      {/* ── Responsive overrides ── */}
      <style>{`
        *,*::before,*::after{box-sizing:border-box}
        html{scroll-behavior:smooth}
        body{margin:0}

        .odc-nav-links{display:flex}
        .odc-mob-btn{display:none}
        @media(max-width:768px){
          .odc-nav-links{display:none}
          .odc-mob-btn{display:flex}
        }

        .odc-how-grid{grid-template-columns:repeat(3,1fr)}
        @media(max-width:900px){.odc-how-grid{grid-template-columns:1fr}}

        .odc-agents-grid{grid-template-columns:repeat(5,1fr)}
        @media(max-width:1100px){.odc-agents-grid{grid-template-columns:repeat(3,1fr)}}
        @media(max-width:640px) {.odc-agents-grid{grid-template-columns:repeat(2,1fr)}}

        .odc-price-grid{grid-template-columns:repeat(5,1fr)}
        @media(max-width:1200px){.odc-price-grid{grid-template-columns:repeat(3,1fr)}}
        @media(max-width:900px){.odc-price-grid{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:580px) {.odc-price-grid{grid-template-columns:1fr}}

        .odc-hero-wrap{display:flex;flex-direction:row;align-items:center;justify-content:space-between;gap:48px;width:100%;max-width:1200px}
        @media(max-width:1024px){.odc-hero-wrap{flex-direction:column;align-items:flex-start}}
        .odc-hero-preview{display:block}
        @media(max-width:1024px){.odc-hero-preview{display:none}}

        .odc-features-6{display:grid;grid-template-columns:repeat(2,1fr);gap:22px;max-width:1100px;margin:0 auto}
        @media(max-width:900px){.odc-features-6{grid-template-columns:1fr}}
        .odc-feature-6-card{transition:transform 0.3s ease,border-color 0.3s ease,box-shadow 0.3s ease}
        .odc-feature-6-card:hover{transform:translateY(-6px);border-color:rgba(190,81,3,0.4)!important;box-shadow:0 16px 48px rgba(190,81,3,0.12)}

        .odc-day-timeline{display:flex;flex-wrap:wrap;align-items:flex-start;justify-content:center;gap:8px 0;margin-top:32px;max-width:1000px;margin-left:auto;margin-right:auto}
        .odc-day-node{display:flex;align-items:center;gap:10px;font-size:12px;color:${MUTED}}
        .odc-day-dot{width:10px;height:10px;border-radius:50%;background:${ACCENT};flex-shrink:0}

        .odc-compare-table{width:100%;border-collapse:collapse;font-size:12px;margin-top:28px}
        .odc-compare-table th,.odc-compare-table td{border:1px solid ${BORDER};padding:10px 8px;text-align:center}
        .odc-compare-table th:first-child,.odc-compare-table td:first-child{text-align:left;color:${TEXT}}

        .odc-testi-grid{grid-template-columns:repeat(3,1fr)}
        @media(max-width:900px){.odc-testi-grid{grid-template-columns:1fr}}

        .odc-features-grid{grid-template-columns:repeat(4,1fr)}
        @media(max-width:1100px){.odc-features-grid{grid-template-columns:repeat(3,1fr)}}
        @media(max-width:700px){.odc-features-grid{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:560px){.odc-features-grid{grid-template-columns:1fr}}
        .odc-platform-feature-card{
          transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
        }
        .odc-platform-feature-card:hover{
          transform: translateY(-4px);
          border-color: rgba(190,81,3,0.45) !important;
          box-shadow: 0 12px 40px rgba(190,81,3,0.12);
        }

        .odc-stats-row{flex-wrap:wrap;gap:40px}
        @media(max-width:480px){.odc-stats-row{gap:28px}}

        /* card hover via CSS so React inline style doesn't fight */
        .odc-hover-card:hover{
          background: ${CARD_HOVER} !important;
          border-color: ${BORDER2} !important;
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(190,81,3,0.12);
        }
        .odc-price-card:hover{
          transform: translateY(-6px);
          box-shadow: 0 20px 60px rgba(190,81,3,0.16);
        }
        .odc-price-highlight{
          border-color: rgba(190,81,3,0.45) !important;
          background: rgba(190,81,3,0.07) !important;
          box-shadow: 0 0 48px rgba(190,81,3,0.18), inset 0 1px 0 rgba(255,255,255,0.08);
        }
        .odc-cta-btn:hover{
          transform:translateY(-2px);
          box-shadow:0 12px 36px rgba(190,81,3,0.45);
        }
        .odc-ghost-btn:hover{
          background:rgba(255,255,255,0.08) !important;
          border-color:rgba(255,255,255,0.18) !important;
        }
        .odc-nav-link:hover{color:#fff !important}

        /* mobile menu */
        .odc-mobile-menu{
          position:fixed;top:64px;left:0;right:0;
          background:rgba(2,12,27,0.97);
          backdrop-filter:blur(24px);
          border-bottom:1px solid ${BORDER};
          padding:20px 5% 28px;
          z-index:99;
          display:flex;flex-direction:column;gap:4px;
        }
        .odc-mobile-link{
          font-size:16px;font-weight:600;
          color:${MUTED};text-decoration:none;
          padding:12px 0;border-bottom:1px solid ${BORDER};
        }
        .odc-mobile-link:hover{color:#fff}

        @keyframes heroFadeUp{
          from{opacity:0;transform:translateY(32px)}
          to{opacity:1;transform:translateY(0)}
        }
        @keyframes orbPulse{
          0%,100%{transform:scale(1);opacity:0.6}
          50%{transform:scale(1.12);opacity:1}
        }
        @keyframes orbFloat{
          0%,100%{transform:translateY(0) scale(1)}
          50%{transform:translateY(-24px) scale(1.04)}
        }
        .odc-orb{
          position:fixed;border-radius:50%;filter:blur(70px);
          pointer-events:none;z-index:0;will-change:transform;
        }
        .odc-hero-badge{animation:heroFadeUp 0.6s 0.1s cubic-bezier(0.4,0,0.2,1) both}
        .odc-hero-h1a{animation:heroFadeUp 0.6s 0.18s cubic-bezier(0.4,0,0.2,1) both}
        .odc-hero-h1b{animation:heroFadeUp 0.6s 0.26s cubic-bezier(0.4,0,0.2,1) both}
        .odc-hero-sub{animation:heroFadeUp 0.6s 0.44s cubic-bezier(0.4,0,0.2,1) both}
        .odc-hero-btns{animation:heroFadeUp 0.6s 0.54s cubic-bezier(0.4,0,0.2,1) both}
        .odc-hero-scroll{animation:heroFadeUp 0.6s 0.7s cubic-bezier(0.4,0,0.2,1) both}

        @keyframes scrollBounce{
          0%,100%{transform:translateX(-50%) translateY(0)}
          50%{transform:translateX(-50%) translateY(8px)}
        }
        .odc-scroll-caret{animation:scrollBounce 2s ease-in-out infinite}
      `}</style>

      <div style={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden', background: BG, color: TEXT, fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}>

        {/* particles */}
        <ParticleCanvas />

        {/* grid overlay */}
        <div aria-hidden style={{ position: 'fixed', inset: 0, backgroundImage: `linear-gradient(rgba(190,81,3,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(190,81,3,0.04) 1px,transparent 1px)`, backgroundSize: '44px 44px', pointerEvents: 'none', zIndex: 0 }} />

        {/* ambient orbs */}
        <div className="odc-orb" aria-hidden style={{ width: 500, height: 500, top: '-10%', right: '-12%', background: 'rgba(190,81,3,0.10)', animation: 'orbFloat 13s ease-in-out infinite' }} />
        <div className="odc-orb" aria-hidden style={{ width: 360, height: 360, bottom: '5%', left: '-10%', background: 'rgba(6,148,148,0.07)', animation: 'orbFloat 17s ease-in-out infinite reverse' }} />
        <div className="odc-orb" aria-hidden style={{ width: 240, height: 240, top: '45%', left: '50%', background: 'rgba(255,206,27,0.05)', animation: 'orbPulse 9s ease-in-out infinite' }} />

        {/* ═══ NAVBAR ═══ */}
        <header style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 5%', height: 64, background: scrolled ? 'rgba(2,12,27,0.93)' : 'transparent', backdropFilter: scrolled ? 'blur(24px)' : 'none', WebkitBackdropFilter: scrolled ? 'blur(24px)' : 'none', borderBottom: scrolled ? `1px solid ${BORDER}` : 'none', transition: 'all 0.3s ease' }}>

          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: TEXT }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#BE5103,#FFCE1B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, boxShadow: '0 4px 16px rgba(190,81,3,0.4)' }}>🤖</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>OnlineDigitalCares</div>
              <div style={{ fontSize: 10, color: MUTED, letterSpacing: '0.1em', lineHeight: 1 }}>AI MARKETING PLATFORM</div>
              <div style={{ fontSize: 10, color: MUTED, letterSpacing: '0.08em', marginTop: 2 }}>Your business never sleeps. 🌙</div>
            </div>
          </Link>

          <nav className="odc-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
            {[
              { label: 'How it works', href: '#how-it-works' },
              { label: 'Features', href: '#features' },
              { label: 'Pricing', href: '#pricing' },
              { label: 'FAQ', href: '/faq' },
            ].map((item) => (
              <a key={item.label} href={item.href} className="odc-nav-link" style={{ fontSize: 14, fontWeight: 500, color: MUTED, textDecoration: 'none', transition: 'color 0.2s' }}>{item.label}</a>
            ))}
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link to="/login" className="odc-nav-link" style={{ fontSize: 14, fontWeight: 600, color: MUTED, textDecoration: 'none', padding: '8px 12px', transition: 'color 0.2s' }}>Login</Link>
            <Link to="/login" className="odc-cta-btn" style={{ fontSize: 13, fontWeight: 700, color: '#000', textDecoration: 'none', padding: '10px 20px', borderRadius: 12, background: 'linear-gradient(135deg,#BE5103,#B7410E)', boxShadow: '0 4px 16px rgba(190,81,3,0.4)', transition: 'all 0.25s ease' }}>Start Free Trial</Link>
            <button type="button" aria-label="Toggle theme" onClick={toggle} className="odc-theme-btn" style={{ color: TEXT, cursor: 'pointer', transition: 'all 0.25s ease' }}>{colorMode === 'dark' ? '☀️' : '🌙'}</button>
            <button type="button" aria-label="Menu" className="odc-mob-btn" onClick={() => setMobileMenu(v => !v)} style={{ width: 40, height: 40, borderRadius: 10, border: `1px solid ${BORDER}`, background: CARD, cursor: 'pointer', display: 'none', alignItems: 'center', justifyContent: 'center', color: TEXT, fontSize: 18 }}>☰</button>
          </div>
        </header>

        {/* mobile menu */}
        {mobileMenu && (
          <div className="odc-mobile-menu">
            {[
              { label: 'How it works', href: '#how-it-works' },
              { label: 'Features', href: '#features' },
              { label: 'Pricing', href: '#pricing' },
              { label: 'FAQ', href: '/faq' },
            ].map((item) => (
              <a key={item.label} href={item.href} className="odc-mobile-link" onClick={() => setMobileMenu(false)}>{item.label}</a>
            ))}
            <Link to="/login" style={{ marginTop: 12, fontSize: 14, fontWeight: 700, color: '#000', textDecoration: 'none', padding: '12px 20px', borderRadius: 12, background: 'linear-gradient(135deg,#BE5103,#B7410E)', boxShadow: '0 4px 16px rgba(190,81,3,0.4)', textAlign: 'center' }} onClick={() => setMobileMenu(false)}>Start Free Trial</Link>
          </div>
        )}

        <div style={{ position: 'relative', zIndex: 10 }}>

          {/* ═══ HERO ═══ */}
          <section style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 8%', paddingTop: 88, paddingBottom: 48, boxSizing: 'border-box', position: 'relative' }}>

            <div className="odc-hero-wrap">
              <div style={{ flex: '1 1 55%', minWidth: 0 }}>
                <div className="odc-hero-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, border: '1px solid rgba(190,81,3,0.35)', background: 'rgba(190,81,3,0.1)', marginBottom: 24 }}>
                  <span style={{ fontSize: 13 }}>🚀</span>
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: '#E8721A' }}>{'India\u2019s #1 AI Marketing Platform'}</span>
                </div>

                <h1 className="odc-hero-line odc-hero-h1a" style={{ margin: 0, fontSize: 'clamp(2.4rem,6vw,4.8rem)', fontWeight: 900, lineHeight: 1.04, letterSpacing: '-0.03em', ...gradientText(`linear-gradient(135deg,${ACCENT},${RUST})`) }}>One Platform.</h1>
                <h1 className="odc-hero-line odc-hero-h1b" style={{ margin: '6px 0 0', fontSize: 'clamp(2.4rem,6vw,4.8rem)', fontWeight: 900, lineHeight: 1.04, letterSpacing: '-0.03em', ...gradientText(`linear-gradient(135deg,${PURPLE},${ACCENT})`) }}>Zero Effort.</h1>

                <p className="odc-hero-sub" style={{ marginTop: 24, maxWidth: 540, fontSize: 17, lineHeight: 1.7, color: MUTED }}>
                  Your business runs 24/7 — completely on autopilot.<br />
                  Social media, leads, calls, SEO — all fully automated.<br />
                  India's first fully autonomous AI marketing platform.
                </p>

                <div className="odc-hero-btns" style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                  <Link to="/login" className="odc-cta-btn" style={{ fontSize: 15, fontWeight: 700, color: '#000', textDecoration: 'none', padding: '14px 28px', borderRadius: 14, background: 'linear-gradient(135deg,#BE5103,#B7410E)', boxShadow: '0 8px 28px rgba(190,81,3,0.4)', transition: 'all 0.25s ease' }}>
                    Start Free — 14 Days Free Trial →
                  </Link>
                  <button type="button" className="odc-ghost-btn" style={{ fontSize: 15, fontWeight: 600, color: TEXT, padding: '14px 24px', borderRadius: 14, border: `1px solid ${BORDER}`, background: CARD, cursor: 'pointer' }} onClick={() => window.alert('2-min demo video — coming soon!')}>
                    Watch 2-min Demo
                  </button>
                </div>

                <div className="odc-hero-scroll" style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 13, color: TEXT, fontWeight: 700 }}>🏆 Trusted by 500+ Indian businesses</div>
                  <div style={{ fontSize: 11, color: MUTED }}>Mumbai · Delhi · Bangalore · Moradabad · Hyderabad</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex' }}>
                      {['🧑‍💻','👩‍💼','🧑‍🚀','👨‍💼','👩‍💻'].map((av, i) => (
                        <div key={i} style={{ width: 30, height: 30, borderRadius: '50%', border: `2px solid ${BG}`, background: `linear-gradient(135deg,${ACCENT}44,${PURPLE}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, marginLeft: i ? -8 : 0 }}>{av}</div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[1,2,3,4,5].map((s) => <span key={s} style={{ color: '#FFCE1B', fontSize: 13 }}>★</span>)}
                      <span style={{ fontSize: 11, color: MUTED, marginLeft: 4 }}>4.9/5</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="odc-hero-preview" style={{ flex: '0 0 340px', width: 340 }}>
                <div
                  className="odc-hover-card"
                  style={{
                    ...card,
                    width: 340,
                    padding: 20,
                    border: '1px solid rgba(190,81,3,0.25)',
                    boxShadow: '0 0 60px rgba(190,81,3,0.08)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <span style={{ fontSize: 14, fontWeight: 800 }}>🤖 ODC Dashboard</span>
                    <span style={{ fontSize: 11, color: ACCENT, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#069494', boxShadow: '0 0 8px #069494' }} /> Live
                    </span>
                  </div>
                  {[
                    ['🎯 Leads Today', '12', '#BE5103'],
                    ['📱 Posts Published', '5', '#069494'],
                    ['📞 Calls Made', '8', '#FFCE1B'],
                  ].map(([lab, val, col]) => (
                    <div key={String(lab)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
                      <span style={{ fontSize: 13, color: MUTED }}>{lab}</span>
                      <span style={{ fontSize: 18, fontWeight: 900, color: col }}>{val}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      '✅ Instagram post published — 2 min ago',
                      '✅ Lead scored 94/100 — 5 min ago',
                      '✅ WhatsApp broadcast sent — 12 min ago',
                    ].map((line) => (
                      <div key={line} style={{ fontSize: 11, color: MUTED, borderLeft: '2px solid #BE5103', paddingLeft: 8, lineHeight: 1.45 }}>{line}</div>
                    ))}
                  </div>
                  <p style={{ margin: '14px 0 0', fontSize: 10, color: '#FFCE1B', fontWeight: 700 }}>All agents running • 24/7 mode</p>
                </div>
              </div>
            </div>

            <div className="odc-hero-scroll" style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: MUTED, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Scroll</span>
              <div className="odc-scroll-caret" style={{ fontSize: 20, color: ACCENT }}>↓</div>
            </div>
          </section>

          {/* ═══ STATS BAR ═══ */}
          <FadeSection>
            <section style={{ borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, background: BG2, padding: '52px 8%' }}>
              <div className="odc-stats-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 40, maxWidth: 1100, margin: '0 auto' }}>
                {[
                  { val: '500+', label: 'Happy Clients', color: '#BE5103' },
                  { val: '24 / 7', label: 'Always Running', color: '#FFCE1B' },
                  { val: '14 Days', label: 'Free Trial', color: '#069494' },
                  { val: '3×', label: 'Avg Lead Growth', color: '#B7410E' },
                ].map(({ val, label, color }) => (
                  <div key={label} style={{ textAlign: 'center', flex: '1 1 140px' }}>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.5rem)', fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
                    <div style={{ marginTop: 8, fontSize: 13, color: MUTED }}>{label}</div>
                  </div>
                ))}
              </div>
            </section>
          </FadeSection>

          {/* ═══ HOW IT WORKS ═══ */}
          <section id="how-it-works" style={{ padding: '100px 8%', background: BG }}>
            <FadeSection>
              <div style={{ textAlign: 'center', marginBottom: 56 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', borderRadius: 999, border: '1px solid rgba(190,81,3,0.28)', background: 'rgba(190,81,3,0.07)', marginBottom: 16 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', color: ACCENT, textTransform: 'uppercase' }}>Simple as 1-2-3</span>
                </div>
                <h2 style={{ margin: 0, fontSize: 'clamp(1.8rem,3.5vw,2.5rem)', fontWeight: 800, letterSpacing: '-0.02em' }}>How it works</h2>
                <p style={{ margin: '14px auto 0', maxWidth: 460, fontSize: 15, color: MUTED, lineHeight: 1.6 }}>Three steps from signup to autonomous growth.</p>
              </div>
            </FadeSection>
            <div className="odc-how-grid" style={{ display: 'grid', gap: 24, maxWidth: 1100, margin: '0 auto' }}>
              {[
                { step: '01', icon: '⚡', title: '30-Min Onboarding', body: 'Capture your business DNA — industry, brand voice, and goals. Set it once; ODC AI handles the rest.' },
                { step: '02', icon: '🔗', title: 'Connect Everything', body: 'Connect social media, WhatsApp, Google, and ads in one place — no technical expertise required.' },
                { step: '03', icon: '🌙', title: 'Rest Easy!', body: 'Your agents keep working overnight. By morning, leads, posts, and calls are already in motion.' },
              ].map((s, i) => (
                <FadeSection key={s.step} style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="odc-hover-card" style={{ ...card, display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(190,81,3,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{s.icon}</div>
                      <span style={{ fontSize: 40, fontWeight: 900, color: 'rgba(255,255,255,0.05)', fontFamily: "'Space Grotesk',sans-serif", lineHeight: 1 }}>{s.step}</span>
                    </div>
                    <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>{s.title}</h3>
                    <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.65, color: MUTED }}>{s.body}</p>
                  </div>
                </FadeSection>
              ))}
            </div>
            <FadeSection>
              <div style={{ marginTop: 48, textAlign: 'center' }}>
                <p style={{ margin: '0 0 16px', fontSize: 12, fontWeight: 800, letterSpacing: '0.14em', color: ACCENT, textTransform: 'uppercase' }}>What happens on Day 1</p>
                <div className="odc-day-timeline">
                  {[
                    ['9 AM', 'Agents activate'],
                    ['11 AM', 'First posts published'],
                    ['2 PM', 'Lead calls start'],
                    ['6 PM', 'Daily report ready'],
                    ['11 PM', 'Night content scheduled'],
                  ].map(([time, action], idx, arr) => (
                    <React.Fragment key={time}>
                      <div className="odc-day-node">
                        <span className="odc-day-dot" />
                        <div>
                          <div style={{ fontWeight: 800, color: TEXT, fontSize: 11 }}>{time}</div>
                          <div>{action}</div>
                        </div>
                      </div>
                      {idx < arr.length - 1 ? (
                        <span style={{ color: BORDER, fontSize: 18, padding: '0 4px', userSelect: 'none' }}>→</span>
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </FadeSection>
          </section>

          {/* ═══ MAIN FEATURES (replaces agent categories) ═══ */}
          <section id="features" style={{ padding: '100px 8%', background: BG2, borderTop: `1px solid ${BORDER}` }}>
            <FadeSection>
              <div style={{ textAlign: 'center', marginBottom: 48 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', borderRadius: 999, border: `1px solid rgba(6,148,148,0.25)`, background: 'rgba(6,148,148,0.07)', marginBottom: 16 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', color: CYAN, textTransform: 'uppercase' }}>Platform</span>
                </div>
                <h2 style={{ margin: 0, fontSize: 'clamp(1.8rem,3.5vw,2.5rem)', fontWeight: 800, letterSpacing: '-0.02em' }}>What ODC Can Do for You</h2>
                <p style={{ margin: '14px auto 0', maxWidth: 560, fontSize: 15, color: MUTED, lineHeight: 1.6 }}>Everything you need to scale your business — in one platform</p>
              </div>
            </FadeSection>
            <div className="odc-features-6">
              {mainFeatureCards.map((c, i) => (
                <FadeSection key={c.title} style={{ transitionDelay: `${(i % 3) * 0.06}s` }}>
                  <div
                    className="odc-hover-card odc-feature-6-card"
                    style={{
                      ...card,
                      padding: 28,
                      border: c.border,
                      borderRadius: 20,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                      height: '100%',
                    }}
                  >
                    <div style={{ fontSize: 48, lineHeight: 1 }}>{c.icon}</div>
                    <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{c.title}</h3>
                    <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: MUTED }}>{c.body}</p>
                    <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {c.items.map((it) => (
                        <li key={it} style={{ display: 'flex', gap: 6, fontSize: 12, color: MUTED, alignItems: 'flex-start' }}>
                          <span style={{ color: ACCENT, fontSize: 12, flexShrink: 0 }}>✓</span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeSection>
              ))}
            </div>
          </section>

          {/* ═══ PLATFORM FEATURES ═══ */}
          <section
            style={{
              padding: '100px 8%',
              background: BG2,
              borderTop: `1px solid ${BORDER}`,
            }}
          >
            <FadeSection>
              <div style={{ textAlign: 'center', marginBottom: 56 }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 14px',
                    borderRadius: 999,
                    border: `1px solid rgba(6,148,148,0.28)`,
                    background: 'rgba(6,148,148,0.08)',
                    marginBottom: 16,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      color: CYAN,
                      textTransform: 'uppercase',
                    }}
                  >
                    India-first
                  </span>
                </div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: 'clamp(1.8rem,3.5vw,2.5rem)',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                  }}
                >
                  India Ka Pehla — Duniya Ka Best
                </h2>
                <p
                  style={{
                    margin: '14px auto 0',
                    maxWidth: 560,
                    fontSize: 15,
                    color: MUTED,
                    lineHeight: 1.6,
                  }}
                >
                  Breakthrough capabilities not available in traditional Indian marketing platforms
                </p>
              </div>
            </FadeSection>
            <div
              className="odc-features-grid"
              style={{ display: 'grid', gap: 22, maxWidth: 1200, margin: '0 auto' }}
            >
              {(
                [
                  {
                    icon: '🔍',
                    iconBg: `${CYAN}22`,
                    title: 'GEO & AEO Visibility',
                    body: 'Appear in ODC AI answers automatically',
                    badge: 'Live',
                    badgeBg: `${ACCENT}22`,
                    badgeColor: ACCENT,
                  },
                  {
                    icon: '🌐',
                    iconBg: `${ACCENT}22`,
                    title: 'AI WebBuilder',
                    body: 'Complete website built by AI in 2 minutes. Hosted, live & SEO-optimized.',
                    badge: 'New',
                    badgeBg: `${ACCENT}22`,
                    badgeColor: ACCENT,
                  },
                  {
                    icon: '🧪',
                    iconBg: `${FEATURE_PURPLE}28`,
                    title: 'Campaign Simulator',
                    body: 'Test on 10,000 AI customers before spending a single rupee',
                    badge: 'Beta',
                    badgeBg: `${FEATURE_PURPLE}28`,
                    badgeColor: '#C4B5FD',
                  },
                  {
                    icon: '🎙️',
                    iconBg: `${ORANGE}28`,
                    title: 'Bharat Voice AI',
                    body: 'AI calls in Hindi, Urdu, Bhojpuri, Punjabi & Tamil — 9AM to 6PM',
                    badge: 'Beta',
                    badgeBg: `${ACCENT}22`,
                    badgeColor: ACCENT,
                  },
                  {
                    icon: '💬',
                    iconBg: 'rgba(190,81,3,0.2)',
                    title: 'WhatsApp Commerce',
                    body: 'Take orders, payments & delivery updates via WhatsApp — fully automated',
                    badge: 'Beta',
                    badgeBg: `${ACCENT}22`,
                    badgeColor: ACCENT,
                  },
                  {
                    icon: '🛡️',
                    iconBg: `${RED}28`,
                    title: 'Brand Safety Shield',
                    body: '24/7 reputation monitoring. Negative content detected & countered instantly.',
                    badge: 'Beta',
                    badgeBg: `${ACCENT}22`,
                    badgeColor: ACCENT,
                  },
                  {
                    icon: '🌍',
                    iconBg: `${CYAN}22`,
                    title: 'NRI Marketing AI',
                    body: 'Target 3.5 crore NRIs in Dubai, UK, USA with timezone-adjusted campaigns',
                    badge: 'New',
                    badgeBg: `${ACCENT}22`,
                    badgeColor: ACCENT,
                  },
                  {
                    icon: '📊',
                    iconBg: `${FEATURE_PURPLE}28`,
                    title: 'Agentic Media Buying',
                    body: 'AI autonomously buys, optimizes and pauses ads. Zero human involvement.',
                    badge: 'New',
                    badgeBg: `${ACCENT}22`,
                    badgeColor: ACCENT,
                  },
                  {
                    icon: '📱',
                    iconBg: 'rgba(249,115,22,0.15)',
                    title: 'AI App Builder',
                    body: 'Android + iOS app generated in 5 minutes. No coding, no agency.',
                    badge: 'New',
                    badgeBg: 'rgba(249,115,22,0.2)',
                    badgeColor: ORANGE,
                  },
                  {
                    icon: '🏪',
                    iconBg: `${FEATURE_PURPLE}28`,
                    title: 'ODC Marketplace',
                    body: 'Premium AI agents, templates & integrations from ODC & community',
                    badge: 'New',
                    badgeBg: `${FEATURE_PURPLE}28`,
                    badgeColor: '#C4B5FD',
                  },
                  {
                    icon: '🔗',
                    iconBg: 'rgba(6,148,148,0.18)',
                    title: 'Blockchain Trust',
                    body: 'Tamper-proof certificates on Polygon blockchain. Trust that cannot be faked.',
                    badge: 'Beta',
                    badgeBg: 'rgba(6,148,148,0.2)',
                    badgeColor: CYAN,
                  },
                ] as const
              ).map((f, i) => (
                <FadeSection key={f.title} style={{ transitionDelay: `${(i % 4) * 0.08}s` }}>
                  <div
                    className="odc-platform-feature-card"
                    style={{
                      background: CARD,
                      border: `1px solid ${BORDER}`,
                      borderRadius: 20,
                      padding: 28,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 16,
                      height: '100%',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: '50%',
                          background: f.iconBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 24,
                        }}
                      >
                        {f.icon}
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          padding: '5px 10px',
                          borderRadius: 8,
                          background: f.badgeBg,
                          color: f.badgeColor,
                          border: `1px solid ${f.badgeColor}44`,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {f.badge}
                      </span>
                    </div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>{f.title}</h3>
                    <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: MUTED, flex: 1 }}>{f.body}</p>
                  </div>
                </FadeSection>
              ))}
            </div>
          </section>

          {/* ═══ SOCIAL PROOF (testimonials) ═══ */}
          <section style={{ padding: '100px 8%', background: BG, borderTop: `1px solid ${BORDER}` }}>
            <FadeSection>
              <div style={{ textAlign: 'center', marginBottom: 52 }}>
                <h2 style={{ margin: 0, fontSize: 'clamp(1.8rem,3.5vw,2.5rem)', fontWeight: 800, letterSpacing: '-0.02em' }}>Their businesses grew — yours can too</h2>
                <p style={{ margin: '14px auto 0', maxWidth: 480, fontSize: 15, color: MUTED }}>Real operators, real results</p>
              </div>
            </FadeSection>
            <div className="odc-testi-grid" style={{ display: 'grid', gap: 20, maxWidth: 1100, margin: '0 auto' }}>
              {testimonials.map((t, i) => (
                <FadeSection key={t.name} style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="odc-hover-card" style={{ ...card, padding: 28 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        marginBottom: 12,
                        fontSize: 11,
                        fontWeight: 800,
                        padding: '4px 10px',
                        borderRadius: 999,
                        border: `1px solid ${BORDER}`,
                        color: MUTED,
                      }}
                    >
                      {t.industry}
                    </span>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <span key={s} style={{ color: '#FFCE1B', fontSize: 14 }}>
                          ★
                        </span>
                      ))}
                    </div>
                    <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: TEXT, fontStyle: 'italic' }}>&ldquo;{t.text}&rdquo;</p>
                    <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg,${ACCENT}44,${PURPLE}44)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 18,
                        }}
                      >
                        {t.avatar}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{t.name}</div>
                        <div style={{ fontSize: 12, color: MUTED }}>{t.role}</div>
                      </div>
                    </div>
                  </div>
                </FadeSection>
              ))}
            </div>
            <FadeSection>
              <div style={{ textAlign: 'center', marginTop: 40 }}>
                <p style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>Trusted by businesses in:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
                  {[
                    'Real Estate',
                    'Restaurants',
                    'Clinics',
                    'Coaching Centers',
                    'Retail Shops',
                    'IT Companies',
                    'Beauty Salons',
                    'Gyms',
                  ].map((pill) => (
                    <span
                      key={pill}
                      style={{
                        fontSize: 11,
                        padding: '6px 12px',
                        borderRadius: 999,
                        border: `1px solid ${BORDER}`,
                        color: MUTED,
                      }}
                    >
                      {pill}
                    </span>
                  ))}
                </div>
              </div>
            </FadeSection>
          </section>

          {/* ═══ PRICING ═══ */}
          <section id="pricing" style={{ padding: '100px 8%', background: BG2, borderTop: `1px solid ${BORDER}` }}>
            <FadeSection>
              <div style={{ textAlign: 'center', marginBottom: 52 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', borderRadius: 999, border: '1px solid rgba(190,81,3,0.28)', background: 'rgba(190,81,3,0.07)', marginBottom: 16 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', color: PURPLE, textTransform: 'uppercase' }}>Pricing</span>
                </div>
                <h2 style={{ margin: 0, fontSize: 'clamp(1.8rem,3.5vw,2.5rem)', fontWeight: 800, letterSpacing: '-0.02em' }}>Seedha Pricing — Koi Chhupa Hua Charge Nahi</h2>
                <p style={{ margin: '14px 0 0', fontSize: 15, color: MUTED }}>14-day free trial. Cancel anytime. Setup fee: ₹0.</p>
              </div>
            </FadeSection>

            <div className="odc-price-grid" style={{ display: 'grid', gap: 20, maxWidth: 1280, margin: '0 auto' }}>
              {pricingPlans.map((p, i) => {
                const isHighlight = p.badge === 'most-popular';
                return (
                  <FadeSection key={p.name} style={{ transitionDelay: `${i * 0.06}s` }}>
                    <div
                      className={`odc-price-card${isHighlight ? ' odc-price-highlight' : ''}`}
                      style={{
                        ...card,
                        background: CARD,
                        textAlign: 'center',
                        padding: '32px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                        height: '100%',
                        position: 'relative',
                        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                      }}
                    >
                      {isHighlight ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg,#BE5103,#B7410E)',
                            color: '#000',
                            fontSize: 10,
                            fontWeight: 800,
                            padding: '4px 14px',
                            borderRadius: 999,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 4px 16px rgba(190,81,3,0.45)',
                          }}
                        >
                          ★ Most Popular
                        </div>
                      ) : null}
                      <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', color: MUTED, textTransform: 'uppercase' }}>{p.name}</p>
                      <p
                        style={{
                          margin: '16px 0 0',
                          fontSize: p.price === 'Talk to us' ? 22 : 30,
                          fontWeight: 900,
                          fontFamily: "'Space Grotesk',sans-serif",
                          letterSpacing: '-0.02em',
                          color: isHighlight ? '#FFCE1B' : TEXT,
                        }}
                      >
                        {p.price}
                        <span style={{ fontSize: 14, fontWeight: 500, color: MUTED }}>{p.period}</span>
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: 13, color: MUTED }}>{p.blurb}</p>
                      <div style={{ margin: '24px 0', height: 1, background: BORDER }} />
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', flex: 1 }}>
                        {p.features.map((f) => (
                          <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: MUTED }}>
                            <span style={{ color: ACCENT, fontSize: 15, flexShrink: 0 }}>✓</span>
                            {f}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={p.name === 'Enterprise' || p.name === 'Agency' ? '/contact' : '/login'}
                        className={isHighlight ? 'odc-cta-btn' : 'odc-ghost-btn'}
                        style={{
                          marginTop: 28,
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 700,
                          color: isHighlight ? '#000' : '#fff',
                          textDecoration: 'none',
                          padding: '12px 20px',
                          borderRadius: 12,
                          background: isHighlight ? 'linear-gradient(135deg,#BE5103,#B7410E)' : CARD,
                          border: isHighlight ? 'none' : `1px solid ${BORDER}`,
                          boxShadow: isHighlight ? '0 6px 20px rgba(190,81,3,0.35)' : 'none',
                          transition: 'all 0.25s ease',
                        }}
                      >
                        {p.cta}
                      </Link>
                    </div>
                  </FadeSection>
                );
              })}
            </div>

            <FadeSection>
              <div style={{ maxWidth: 960, margin: '40px auto 0', overflowX: 'auto' }}>
                <table className="odc-compare-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Starter</th>
                      <th>Growth</th>
                      <th>Pro</th>
                      <th>Enterprise</th>
                      <th>Agency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planCompareRows.map((row) => (
                      <tr key={row.feature}>
                        <td>{row.feature}</td>
                        <td style={{ color: row.starter ? ACCENT : MUTED }}>{row.starter ? '✓' : '✗'}</td>
                        <td style={{ color: row.growth ? ACCENT : MUTED }}>{row.growth ? '✓' : '✗'}</td>
                        <td style={{ color: row.pro ? ACCENT : MUTED }}>{row.pro ? '✓' : '✗'}</td>
                        <td style={{ color: row.enterprise ? ACCENT : MUTED }}>{row.enterprise ? '✓' : '✗'}</td>
                        <td style={{ color: row.agency ? ACCENT : MUTED }}>{row.agency ? '✓' : '✗'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeSection>
          </section>

          {/* ═══ FAQ PREVIEW ═══ */}
          <section id="faq-preview" style={{ padding: '80px 8%', background: BG, borderTop: `1px solid ${BORDER}` }}>
            <FadeSection>
              <div style={{ textAlign: 'center', marginBottom: 36 }}>
                <h2 style={{ margin: 0, fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800 }}>Common Questions</h2>
              </div>
            </FadeSection>
            <div style={{ maxWidth: 720, margin: '0 auto' }}>
              {landingFaqPreview.map((faq) => {
                const open = landingFaqOpen === faq.id;
                return (
                  <div
                    key={faq.id}
                    style={{
                      border: `1px solid ${open ? 'rgba(190,81,3,0.3)' : BORDER}`,
                      borderRadius: 16,
                      marginBottom: 8,
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setLandingFaqOpen(open ? null : faq.id)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        padding: '18px 22px',
                        cursor: 'pointer',
                        background: open ? 'rgba(190,81,3,0.04)' : CARD,
                        border: 'none',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: 15, color: TEXT, paddingRight: 16 }}>{faq.q}</span>
                      <span style={{ color: ACCENT, fontSize: 12, transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>▼</span>
                    </button>
                    {open ? (
                      <div
                        style={{
                          padding: '0 22px 18px',
                          fontSize: 14,
                          lineHeight: 1.7,
                          color: MUTED,
                          borderTop: `1px solid ${BORDER}`,
                          paddingTop: 14,
                        }}
                      >
                        {faq.a}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Link to="/faq" style={{ fontSize: 14, fontWeight: 700, color: ACCENT, textDecoration: 'none' }}>
                  Need more answers? →
                </Link>
              </div>
            </div>
          </section>

          {/* ═══ CTA BAND ═══ */}
          <FadeSection>
            <section style={{ padding: '80px 8%', background: BG2, borderTop: `1px solid ${BORDER}` }}>
              <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center', padding: '56px 36px', borderRadius: 28, border: '1px solid rgba(190,81,3,0.3)', background: 'rgba(190,81,3,0.05)', position: 'relative', overflow: 'hidden' }}>
                <div aria-hidden style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, height: 200, background: 'rgba(190,81,3,0.12)', borderRadius: '50%', filter: 'blur(60px)', pointerEvents: 'none' }} />
                <p style={{ position: 'relative', margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.18em', color: ACCENT, textTransform: 'uppercase' }}>ODC</p>
                <h2 style={{ position: 'relative', margin: '14px 0 0', fontSize: 'clamp(1.75rem,4vw,2.6rem)', fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.15, ...gradientText('linear-gradient(135deg,#BE5103,#FFCE1B,#069494)') }}>
                  Start Today — See Results Tomorrow
                </h2>
                <p style={{ position: 'relative', margin: '14px auto 0', maxWidth: 460, fontSize: 15, color: MUTED, lineHeight: 1.65 }}>14-day free trial. No credit card required. Setup fee ₹0.</p>
                <div
                  style={{
                    position: 'relative',
                    marginTop: 16,
                    display: 'inline-block',
                    padding: '6px 14px',
                    borderRadius: 999,
                    background: 'rgba(245,158,11,0.15)',
                    color: '#FBBF24',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  ⚡ Limited time: First 3 months pe 30% discount
                </div>
                <div style={{ position: 'relative', marginTop: 28, display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/login" className="odc-cta-btn" style={{ fontSize: 15, fontWeight: 700, color: '#000', textDecoration: 'none', padding: '14px 28px', borderRadius: 14, background: 'linear-gradient(135deg,#BE5103,#B7410E)', boxShadow: '0 8px 28px rgba(190,81,3,0.45)', transition: 'all 0.25s ease' }}>
                    Start Free Trial — 14 Days →
                  </Link>
                  <a
                    href="https://wa.me/919634405161"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="odc-ghost-btn"
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#fff',
                      padding: '14px 22px',
                      borderRadius: 14,
                      border: 'none',
                      background: '#25D366',
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                  >
                    Talk to Us on WhatsApp
                  </a>
                </div>
              </div>
            </section>
          </FadeSection>

          {/* ═══ FOOTER ═══ */}
          <footer style={{ borderTop: `1px solid ${BORDER}`, padding: '48px 8% 28px', background: BG2 }}>
            <div
              style={{
                maxWidth: 1200,
                margin: '0 auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 32,
              }}
              className="odc-footer-grid"
            >
              <style>{`
                @media(max-width:900px){
                  .odc-footer-grid{grid-template-columns:repeat(2,1fr)!important}
                }
                @media(max-width:520px){
                  .odc-footer-grid{grid-template-columns:1fr!important}
                }
              `}</style>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg,#BE5103,#FFCE1B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🤖</div>
                  <span style={{ fontSize: 15, fontWeight: 800 }}>OnlineDigitalCares</span>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 700, color: ACCENT }}>The Future of AI Marketing</p>
                <p style={{ margin: '8px 0 0', fontSize: 11, color: MUTED }}>Your business never sleeps. 🌙</p>
                <p style={{ marginTop: 14, fontSize: 11, color: MUTED, letterSpacing: '0.06em' }}>FB · IG · YT · LI · WA</p>
              </div>
              <div>
                <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: MUTED, textTransform: 'uppercase' }}>Platform</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    ['Dashboard', '/dashboard'],
                    ['AI Agents', '/agents'],
                    ['Leads', '/leads'],
                    ['Calendar', '/calendar'],
                    ['AI Calls', '/calls'],
                    ['Analytics', '/analytics'],
                  ].map(([lab, href]) => (
                    <Link key={String(lab)} to={href} style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}>
                      {lab}
                    </Link>
                  ))}
                </div>
              </div>
              <div>
                <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: MUTED, textTransform: 'uppercase' }}>Features</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    ['GEO Score', '/geo'],
                    ['WebBuilder', '/webbuilder'],
                    ['Campaign Simulator', '/simulator'],
                    ['WhatsApp Commerce', '/whatsapp-commerce'],
                    ['Festive AI', '/festive'],
                    ['Bharat Voice', '/bharat-voice'],
                  ].map(([lab, href]) => (
                    <Link key={String(lab)} to={href} style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}>
                      {lab}
                    </Link>
                  ))}
                </div>
              </div>
              <div>
                <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: MUTED, textTransform: 'uppercase' }}>Company</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={{ fontSize: 13, color: MUTED }}>About</span>
                  <Link to="/pricing" style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}>
                    Pricing
                  </Link>
                  <Link to="/faq" style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}>
                    FAQ
                  </Link>
                  <Link to="/support" style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}>
                    Support
                  </Link>
                  <a href="https://onlinedigitalcares.com/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}>
                    Privacy
                  </a>
                  <a href="https://onlinedigitalcares.com/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}>
                    Terms
                  </a>
                  <a href="mailto:support@onlinedigitalcares.com" style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}>
                    Contact
                  </a>
                </div>
              </div>
            </div>
            <div
              style={{
                maxWidth: 1200,
                margin: '32px auto 0',
                paddingTop: 20,
                borderTop: `1px solid ${BORDER}`,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                gap: 12,
                fontSize: 12,
                color: MUTED,
              }}
            >
              <span>© 2026 OnlineDigitalCares.com · Made with ❤️ in India 🇮🇳</span>
              <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <a href="https://onlinedigitalcares.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: MUTED, textDecoration: 'none' }}>
                  Privacy
                </a>
                <a href="https://onlinedigitalcares.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: MUTED, textDecoration: 'none' }}>
                  Terms
                </a>
                <a href="mailto:support@onlinedigitalcares.com" style={{ color: MUTED, textDecoration: 'none' }}>
                  Contact
                </a>
              </span>
            </div>
          </footer>

        </div>
      </div>
    </>
  );
}
